// OfferFormView.tsx
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createTenderSchema, TenderFormData } from "../../tender-schema";
import TenderFieldView from "./tender-field-view";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { Form } from "@/app/global-components/form";
import { Separator } from "@/app/global-components/separator";
import regionsData from "@/app/features/company/data/regions_lite.json";
import citiesData from "@/app/features/company/data/cities_lite.json";
import OfferSubmitView from "./tender-submit-view";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { isEmpty } from "lodash";
import AddIcon from "@mui/icons-material/Add";
import CompanyWarningPopup from "@/app/features/CompanyCreate/companycreatePopup";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { routes } from "@/consts";
import useRegisterCompany from "@/hooks/use-register-company";
import useOffer from "@/hooks/useTender";
interface Region {
  region_id: number;
  name_ar: string;
}

interface City {
  city_id: number;
  region_id: number;
  name_ar: string;
}

interface Option {
  value: string;
  label: string;
}
interface Product {
  item: string;
  quantity: number;
  notice: string;
  unit: string;
  attachment: File | null;
}
interface CategoryOption {
  value: any; // Use specific types here, like string or number, if known
  label: any; // Use string if label is always a string
}
const TenderFormView: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const TenderSchema = createTenderSchema(t as (key: string) => string);
  const navigate = useNavigate();
  const { createOffer, getcategories, isSaveingLoading, error } = useOffer(); // Custom hook for offer operations
  const [regions, setRegions] = useState<Option[]>([]);
  const [cities, setCities] = useState<Option[]>([]);
  const [products, setProducts] = useState<Product[]>([
    { item: "", quantity: 1, notice: "", unit: "", attachment: null },
  ]);
  const [Categoris, setCategoris] = useState<CategoryOption[]>([]);
  const [isCompanyAvailable, setIsCompanyAvailable] = useState(false);
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const { getCompanyMine } = useRegisterCompany();
  const form = useForm<z.infer<typeof TenderSchema>>({
    resolver: zodResolver(TenderSchema),
    defaultValues: {
      id: null,
      title: "",
      minValue: 1,
      value: 0,
      endDate: "",
      deliverDate: "",
      type: "Tiny",
      status: null,
      region: "",
      city: "",
      receiveDocumentsType: "Both",
      Paylater: "No",
      contactInfo: "",
      categoriesIds: [],
      fileName: "",
      fileDescription: "",
      fileId: null,
      attachmentRequired: false,
      attachmentName: "",
      attachmentDescription: "",
      attachmentId: null,
      products: [],
      companyId: myCompany?.id,
      TenderNr: myCompany?.TenderNr + 1,
    },
  });
  useEffect(() => {
    const fetchCompany = async () => {
      await getCompanyMine().catch(() => {
        setIsCompanyAvailable(true);
      });
    };
    fetchCompany();
  }, []);
  useEffect(() => {
    setRegions(
      regionsData.map((region: Region) => ({
        value: region.region_id.toString(),
        label: region.name_ar,
      }))
    );
  }, []);

  useEffect(() => {
    const selectedRegion = form.watch("region");
    if (selectedRegion) {
      const filteredCities = citiesData
        .filter((city: City) => city.region_id === parseInt(selectedRegion))
        .map((city: City) => ({
          value: city.city_id.toString(),
          label: city.name_ar,
        }));
      setCities(filteredCities);
    } else {
      setCities([]);
    }
  }, [form.watch("region")]);

  useEffect(() => {
    if (myCompany) {
      form.setValue("companyId", myCompany.id);
      form.setValue("TenderNr", myCompany.TenderNr + 1);
      form.setValue("contactInfo", myCompany.contactInfo);
      form.setValue("region", myCompany.region);
      form.setValue("city", myCompany.city);
    }
  }, [myCompany]);

  const addProduct = () => {
    const newProduct = {
      item: "",
      quantity: 1,
      notice: "",
      unit: "",
      attachment: null,
    };
    const currentProducts = form.getValues("products") as Product[];
    const updatedProducts = [...currentProducts, newProduct];
    setProducts(updatedProducts); // Update local state

    // Update form state
    form.setValue("products", updatedProducts);
  };

  const removeProduct = (index: number) => {
    const currentProducts = form.getValues("products") as Product[];
    const newProducts = currentProducts.filter((_, i) => i !== index);
    setProducts(newProducts);
    form.setValue("products", newProducts);
  };
  const onSubmit = async (values: TenderFormData) => {
    try {
      await createOffer(values);
      navigate(routes.tenders);
    } catch (err) {
      console.error("Error creating offer:", err);
    }
  };
  useEffect(() => {
    const fetchCategoris = async () => {
      try {
        const Categories = await getcategories();
        if (Categories) {
          const uniqueCategories = Categories.data.filter(
            (item: { name: any }, index: any, self: any[]) =>
              index === self.findIndex((t) => t.name === item.name) // Only keep unique ids
          );
          const Cates: CategoryOption[] = uniqueCategories.map(
            (item: { id: any; name: any }) => ({
              value: item?.id,
              label: item.name,
            })
          );
          setCategoris(Cates);
        } else {
          setCategoris([]);
        }
      } catch (error) {
        console.error("Error fetching Offers:", error);
      }
    };
    fetchCategoris();
  }, []);
  return (
    <Form {...form}>
      {/* {isNull(myCompany) ? <CompanyWarningPopup open={true} /> : ""} */}
      {isCompanyAvailable && <CompanyWarningPopup open={true} />}
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-5 w-full md:flex-row space-x-4">
          <section className="w-full">
            <TenderFieldView
              name="title"
              label={t("orders.title1")}
              placeholder={t("orders.placeholders.enterRequestTitle")}
              formControl={form.control}
            />
          </section>
          <section className="w-full !m-0">
            <TenderFieldView
              name="categoriesIds"
              label={t("company.fields.categories")}
              placeholder={t("categories.select")}
              inputType="multiselect"
              options={Categoris}
              formControl={form.control}
            />
          </section>
        </div>
        <div className="flex flex-col gap-5 w-full md:flex-row space-x-4">
          <section className="w-full !m-0">
            <TenderFieldView
              name="value"
              label={t("tender.fields.estimatedValue")}
              placeholder={t("orders.placeholders.enterValue")}
              inputType="number"
              formControl={form.control}
            />
          </section>
          <div className="w-full"></div>
        </div>
        {/*  */}
        <section
          className="w-full md:justify-center"
          style={{ display: "none" }}
        >
          <TenderFieldView
            name="type"
            label={t("offers.type")}
            placeholder={t("offers.selectType")}
            inputType="radio"
            options={[
              { value: "Tiny", label: t("offers.small") },
              { value: "Huge", label: t("offers.large") },
            ]}
            formControl={form.control}
          />
        </section>

        <Separator className="w-full bg-primary-gray h-[2px]" />

        {/* Product Table */}
        <div className="flex relative flex-col gap-5 w-full">
          <div className="flex items-center justify-between mt-4">
            <h3 className="text-lg font-semibold">{t("offers.items")}</h3>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={addProduct}
                className="flex items-center px-4 py-2 bg-secondary-brightYellow text-textcolor-black rounded"
                style={{ background: "#000", color: "#fff" }}
              >
                <AddIcon className="mr-2" />
                {t("actions.addItem")}
              </button>
            </div>
          </div>
          <div className="no-scrollbar overflow-x-auto w-full">
            {products.length > 0 && (
              <table className="min-w-full border-collapse border border-gray-200">
                <thead>
                  <tr>
                    <th className="border min-w-44 xl:min-w-full border-gray-300 p-2">
                      {t("offers.item")}
                    </th>
                    <th className="border  min-w-44 xl:min-w-full border-gray-300 p-2">
                      {t("checkout.fields.quantity")}
                    </th>
                    <th className="border min-w-44 xl:min-w-32 border-gray-300 p-2">
                      {t("item.fields.unit")}
                    </th>
                    <th className="border min-w-44 xl:min-w-full border-gray-300 p-2">
                      {t("orders.fields.notes")}
                    </th>
                    <th className="border min-w-44 xl:min-w-full border-gray-300 p-2">
                      {t("offers.attachment")}
                    </th>
                    <th className="border border-gray-300 p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((_, index) => (
                    <tr key={index}>
                      <td className="border border-gray-300 p-2">
                        <TenderFieldView
                          name={`products.${index}.item`}
                          placeholder={t("offers.placeholders.EnterItemName")}
                          label=""
                          inputType="text"
                          formControl={form.control}
                        />
                      </td>
                      <td className="border border-gray-300 p-2">
                        <TenderFieldView
                          name={`products.${index}.quantity`}
                          label=""
                          placeholder={t("offers.placeholders.EnterQuantity")}
                          inputType="number"
                          formControl={form.control}
                        />
                      </td>
                      <td className="border border-gray-300 p-2">
                        <TenderFieldView
                          name={`products.${index}.unit`}
                          label=""
                          inputType="combobox"
                          placeholder={""}
                          formControl={form.control}
                          options={[
                            { value: "PC", label: "قطعة (Piece - PC)" },
                            { value: "TAB", label: "حبة (Tablet - TAB)" },
                            { value: "TUBE", label: "أنبوب (Tube - TUBE)" },
                            { value: "SYR", label: "حقنة (Syringe - SYR)" },
                            { value: "ML", label: "ملليلتر (Milliliter - ML)" },
                            { value: "L", label: "لتر (Liter - L)" },
                            { value: "GM", label: "غرام (Gram - GM)" },
                            { value: "KG", label: "كيلوغرام (Kilogram - KG)" },
                            { value: "BX", label: "علبة (Box - BX)" },
                            { value: "CTN", label: "كرتون (Carton - CTN)" },
                            { value: "PKG", label: "رزمة (Pack - PKG)" },
                            { value: "TRAY", label: "درج (Tray - TRAY)" },
                            { value: "PR", label: "زوج (Pair - PR)" },
                            { value: "STRIP", label: "شريط (Strip - STRIP)" },
                            {
                              value: "IU",
                              label: "وحدة دولية (International Unit - IU)",
                            },
                          ]}
                        />
                      </td>
                      <td className="border border-gray-300 p-2">
                        <TenderFieldView
                          name={`products.${index}.notice`}
                          label=""
                          placeholder={t("tender.fields.EnterNote")}
                          inputType="textarea"
                          formControl={form.control}
                        />
                      </td>
                      <td className="border border-gray-300 p-2">
                        <TenderFieldView
                          name={`products.${index}.attachment`}
                          placeholder={t("offers.placeholders.SelectFile")}
                          label=""
                          inputType="file"
                          formControl={form.control}
                        />
                      </td>
                      <td className="border border-gray-300 p-2">
                        <button
                          type="button"
                          onClick={() => removeProduct(index)}
                          className="text-red-500"
                        >
                          <DeleteIcon />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <Separator className="w-full mt-4 bg-primary-gray h-[2px]" />

        <div className="flex flex-col mt-4 gap-5 w-full md:flex-row space-x-4">
          <section className="w-full !m-0">
            <TenderFieldView
              name="fileName"
              label={t("attachments.fileName")}
              placeholder={t("offers.placeholders.enterFileName")}
              formControl={form.control}
            />
          </section>
          <section className="w-full !m-0">
            <TenderFieldView
              name="fileDescription"
              label={t("attachments.fileDesc")}
              placeholder={t("offers.placeholders.enterFileDescription")}
              formControl={form.control}
            />
          </section>
          <section className="w-full !m-0">
            <TenderFieldView
              name="fileId"
              label={t("offers.fields.file")}
              placeholder={t("offers.placeholders.SelectFile")}
              inputType="file"
              formControl={form.control}
            />
          </section>
        </div>
        <Separator className="w-full mt-4 bg-primary-gray h-[2px] " />

        <div className="flex flex-col mt-4 gap-5 w-full md:flex-row space-x-4">
          <section className="w-full">
            <TenderFieldView
              name="endDate"
              label={t("offers.fields.submissionDeadline")}
              placeholder={t("offers.placeholders.selectSubmissionDeadline")}
              inputType="date"
              formControl={form.control}
            />
          </section>
          <section className="w-full !m-0">
            <TenderFieldView
              name="deliverDate"
              label={t("offers.fields.openingDate")}
              placeholder={t("offers.placeholders.selectOpeningDate")}
              inputType="date"
              formControl={form.control}
            />
          </section>
        </div>

        <Separator className="w-full mt-4 bg-primary-gray h-[2px] " />

        <div className="flex flex-col mt-4 gap-5 w-full md:flex-row space-x-4">
          <section className="w-full">
            <TenderFieldView
              name="region"
              label={t("company.fields.region")}
              placeholder={t("company.placeholders.selectRegion")}
              inputType="combobox"
              options={regions}
              formControl={form.control}
            />
          </section>
          <section className="w-full !m-0">
            <TenderFieldView
              name="city"
              label={t("company.fields.city")}
              placeholder={t("company.placeholders.selectCity")}
              inputType="combobox"
              options={cities}
              formControl={form.control}
            />
          </section>
        </div>
        <Separator className="w-full mt-4 bg-primary-gray h-[2px] " />
        {/*<section className="flex">
          <TenderFieldView
            name="receiveDocumentsType"
            label="كيفية استلام العظاءات"
            placeholder="اختر نوع استلام العظاءات"
            inputType="radio"
            options={[
              {value: "Both", label: " (عطاء فني مع عطاء مالي) كلاهما"},
              {
                value: "OneByOne",
                label: "العطاء الفني منفصل عن العطاء المالي",
              },
            ]}
            formControl={form.control}
          />
        </section>
        */}
        <section className="flex mt-4">
          <TenderFieldView
            name="Paylater"
            label={t("offers.fields.paymentTerms")}
            placeholder={t("offers.placeholders.selectDeferredPaymentType")}
            inputType="radio"
            options={[
              { value: "Yes", label: t("payment.deferred") },
              {
                value: "No",
                label: t("offers.fields.cashPayment"),
              },
            ]}
            formControl={form.control}
          />
        </section>

        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
        {isEmpty(myCompany) ? (
          <></>
        ) : (
          // <Alert severity="error">{t("offers.companyInfoRequired")}</Alert>
          <OfferSubmitView isLoading={isSaveingLoading} />
        )}
      </form>
    </Form>
  );
};

export default TenderFormView;
